import { useEffect, useRef, useState } from 'react';

interface Options {
  /** Distance (px) the user must drag before refresh fires. */
  threshold?: number;
  /** Called when threshold is exceeded on touchend. */
  onRefresh: () => void;
  /** When false, the hook attaches nothing (e.g., during fetch). */
  enabled?: boolean;
}

export function usePullToRefresh<T extends HTMLElement>(opts: Options) {
  const { threshold = 80, onRefresh, enabled = true } = opts;
  const ref = useRef<T | null>(null);
  const startY = useRef<number | null>(null);
  const [pullDistance, setPullDistance] = useState(0);
  const pullDistanceRef = useRef(0);
  pullDistanceRef.current = pullDistance;

  useEffect(() => {
    const el = ref.current;
    if (!el || !enabled) return;

    const onTouchStart = (e: TouchEvent) => {
      if (el.scrollTop > 0) {
        startY.current = null;
        return;
      }
      startY.current = e.touches[0]?.clientY ?? null;
    };

    const onTouchMove = (e: TouchEvent) => {
      if (startY.current == null) return;
      const dy = (e.touches[0]?.clientY ?? 0) - startY.current;
      if (dy > 0) {
        // damp the pull so it feels rubbery
        setPullDistance(Math.min(dy * 0.5, threshold * 1.5));
      }
    };

    const onTouchEnd = () => {
      if (startY.current == null) return;
      if (pullDistanceRef.current >= threshold) {
        onRefresh();
      }
      startY.current = null;
      setPullDistance(0);
    };

    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchmove', onTouchMove, { passive: true });
    el.addEventListener('touchend', onTouchEnd);
    el.addEventListener('touchcancel', onTouchEnd);

    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', onTouchEnd);
      el.removeEventListener('touchcancel', onTouchEnd);
    };
  }, [enabled, threshold, onRefresh]);

  return { ref, pullDistance, threshold };
}
